'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { Container } from '@eliadmin/core/src/components/ui/container';
import { Card } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FiMapPin, FiMail, FiPhone, FiCheck } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchOffers } from '@eliadmin/core/src/api/jobportal/offers-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { JobOfferCardSmall } from '@eliadmin/core/src/components/jobportal/job-offer-card-small';
import { htmlToPlainText } from '@eliadmin/core/src/utils/html-utils';
import { buildJobOffersQuery } from '@eliadmin/core/src/utils/jobportal-offers-query';
import type { PublicJobPortalCompany } from '@eliadmin/core/src/pages/(jobportal)/job-company/job-company-public';

export type JobInterviewPublicCompany = Omit<PublicJobPortalCompany, 'company_size' | 'country'> & {
  slug: string;
  country: { id: string; name: string } | null;
  tags: Array<{ id: number; name: string; slug: string; color: string | null }>;
  offers_count: number;
  company_size: string | null;
  executive_manager: string | null;
};

type JobCompanyPageProps = {
  jobPortalCompany: JobInterviewPublicCompany;
};

export const JobCompanyPageClient = ({ jobPortalCompany }: JobCompanyPageProps) => {
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);
  const { data: companyOffers } = useQuery({
    queryKey: [CacheKey.JOBPPORTAL_OFFERS, 'company-public', jobPortalCompany.id],
    queryFn: () =>
      fetchOffers({
        query: {
          'order[]': ['desc', 'desc'] as any,
          'orderBy[]': ['topped_up_to', 'published_at'] as any,
          ...buildJobOffersQuery({
            is_paid: true,
            expires_at: true,
            company_ids: [jobPortalCompany.id],
          }),
        } as any,
        locale,
      }),
  });

  return (
    <Container className={cn('flex flex-col gap-8 pb-8', 'ea-job-company-page')}>
      {/* Breadcrumb */}
      <div className="flex-start left-0 mb-4 flex h-8 w-full items-center justify-center bg-white px-2 py-1 md:mb-6 md:h-10 md:px-2 md:py-4">
        <p className="w-full max-w-[1171px] whitespace-nowrap text-sm">
          <Link href="/">Domov</Link> / <Link href="/firmy">Firmy</Link> / {jobPortalCompany.name}
        </p>
      </div>

      {/* Header Section */}
      <div className="flex flex-col items-start justify-center md:h-[212px] md:flex-row md:items-center md:justify-between md:gap-8">
        <div className="flex flex-col items-center md:flex-row md:items-start">
          <Image
            src={jobPortalCompany.image?.url || '/images/placeholder.png'}
            alt={resolveImageAlt(jobPortalCompany?.image, jobPortalCompany?.name)}
            width={300}
            height={300}
            className="h-[300px] w-full rounded-lg object-contain md:h-[200px] md:w-[200px]"
          />
          <div className="flex h-full w-full flex-col items-start justify-start gap-1  md:flex md:flex-col md:gap-4 md:px-8">
            <div className="flex flex-col items-start gap-2 pb-4  md:pb-0">
              {!!jobPortalCompany.tags?.length && (
                <div className="flex flex-wrap gap-1">
                  {jobPortalCompany.tags.map((tag: any) => (
                    <div
                      key={tag.id}
                      className="flex h-[24px] items-center rounded-[8px] bg-primary px-2 text-[10px] font-medium text-white sm:text-xs md:h-[33px]"
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
              )}
              {!!jobPortalCompany.categories?.length && (
                <div className="flex flex-wrap gap-1">
                  {jobPortalCompany.categories?.map((category: any, index: number) => (
                    <div key={index} className="flex">
                      <span className="flex h-[20px] items-center rounded-[8px] bg-primary/10 px-2 text-[8px] font-medium text-primary sm:text-xs md:h-[33px]">
                        {category.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mb-4 flex flex-col gap-2 md:mb-0">
              <h2 className="text-2xl font-bold">{jobPortalCompany.name}</h2>
              {!!(jobPortalCompany.city || jobPortalCompany.country?.id) && (
                <div className="flex items-center gap-1 text-xs text-primary">
                  <FiMapPin size={16} /> {jobPortalCompany.city}
                  {jobPortalCompany.country?.id && `, ${jobPortalCompany.country.id}`}
                </div>
              )}
            </div>
          </div>
        </div>
        {(typeof jobPortalCompany.offers_count === 'number' || jobPortalCompany.www) && (
          <div className="w-full rounded-lg bg-[#f3f3f3] p-6 md:flex md:h-auto md:w-[300px] md:flex-col md:justify-center md:gap-2 md:px-8">
            {typeof jobPortalCompany.offers_count === 'number' && (
              <div className="flex flex-col">
                <p>
                  <strong>
                    <span className="text-primary">{jobPortalCompany.offers_count} </span>
                    {jobPortalCompany.offers_count === 0 ||
                      (jobPortalCompany.offers_count > 4 && 'aktívnych ponúk')}
                    {jobPortalCompany.offers_count === 1 && 'aktívna ponuka'}
                    {jobPortalCompany.offers_count > 1 &&
                      jobPortalCompany.offers_count < 4 &&
                      'aktívne ponuky'}
                  </strong>
                </p>
              </div>
            )}
            {!!jobPortalCompany.www && (
              <div className="flex flex-col">
                <a
                  href={
                    jobPortalCompany.www?.startsWith('http')
                      ? jobPortalCompany.www
                      : `https://${jobPortalCompany.www}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-primary hover:underline"
                >
                  {jobPortalCompany.www.replace('https://www.', '')}
                </a>
              </div>
            )}
            {!!jobPortalCompany.slogan && (
              <div className="flex flex-col gap-1">
                <h3 className="text-sm font-semibold text-primary">Poskytuje prax v</h3>
                <p className="text-sm font-medium text-black">
                  {htmlToPlainText(jobPortalCompany.slogan)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content grid */}
      <div className={cn('grid grid-cols-12 gap-8', 'ea-job-company-page__grid')}>
        <section
          className={cn(
            'col-span-12 flex flex-col gap-4 md:col-span-7',
            'ea-job-company-page__main',
          )}
        >
          <Separator />
          <div className="flex flex-col gap-2">
            <h3 className="mb-2 text-xl font-semibold text-primary">{t('text1821')}</h3>
            {!!jobPortalCompany.description && <HtmlRenderer html={jobPortalCompany.description} />}
          </div>

          {!!jobPortalCompany.interest && (
            <div>
              <h3 className="text-lg font-semibold">{t('text1542')}</h3>
              <div className="mt-2 whitespace-pre-wrap text-sm text-foreground">
                {jobPortalCompany.interest}
              </div>
            </div>
          )}

          {(jobPortalCompany.practice_for_students ||
            jobPortalCompany.cooperation_with_schools) && (
            <ul className="mt-4 space-y-2">
              {jobPortalCompany.practice_for_students && (
                <li className="flex items-center gap-2 text-sm">
                  <FiCheck className="h-6 w-6 text-primary" />
                  <span className="font-bold text-primary">{t('text2466')}</span>
                </li>
              )}
              {jobPortalCompany.cooperation_with_schools && (
                <li className="flex items-center gap-2 text-sm">
                  <FiCheck className="h-6 w-6 text-primary" />
                  <span className="font-bold text-primary">{t('text2467')}</span>
                </li>
              )}
            </ul>
          )}

          {/* Company details - bulleted */}
          <section className="flex flex-col gap-4">
            <ul className="list-disc space-y-3 pl-5 marker:text-primary">
              {!!jobPortalCompany.company_size && (
                <li>
                  <div className="text-sm font-semibold">{t('text1556')}</div>
                  <p className="text-sm">{jobPortalCompany.company_size}</p>
                </li>
              )}
              {!!jobPortalCompany.executive_manager && (
                <li>
                  <div className="text-sm font-semibold">Executive manager</div>
                  <p className="text-sm">{jobPortalCompany.executive_manager}</p>
                </li>
              )}
            </ul>
          </section>

          {/* Socials now in hero/right; contact moved to sidebar */}
        </section>

        {/* Right column */}
        <aside
          className={cn(
            'col-span-12 flex flex-col gap-4 md:col-span-5',
            'ea-job-company-page__aside',
          )}
        >
          <Card variant="outlined" className="p-4">
            <h3 className="mb-3 text-lg font-semibold">{t('text186')}</h3>
            <ul className="list-disc space-y-3 pl-5 marker:text-primary">
              {!!jobPortalCompany.email && (
                <li>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <FiMail className="h-4 w-4 text-primary" /> {t('text35')}
                  </div>
                  <a
                    href={`mailto:${jobPortalCompany.email}`}
                    className="text-base text-primary hover:underline"
                  >
                    {jobPortalCompany.email}
                  </a>
                </li>
              )}
              {!!jobPortalCompany.phone && (
                <li>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <FiPhone className="h-4 w-4 text-primary" /> {t('text36')}
                  </div>
                  <a
                    href={`tel:${jobPortalCompany.phone}`}
                    className="text-base text-primary hover:underline"
                  >
                    {jobPortalCompany.phone}
                  </a>
                </li>
              )}
              {!!jobPortalCompany.ico && (
                <li>
                  <div className="text-sm font-semibold">
                    {t('text42')}: <span className="text-primary">{jobPortalCompany.ico}</span>
                  </div>
                </li>
              )}
              {!!jobPortalCompany.dic && (
                <li>
                  <div className="text-sm font-semibold">
                    {t('text43')}: <span className="text-primary">{jobPortalCompany.dic}</span>
                  </div>
                </li>
              )}
              {!!jobPortalCompany.ic_dph && (
                <li>
                  <div className="text-sm font-semibold">
                    {t('text44')}: <span className="text-primary">{jobPortalCompany.ic_dph}</span>
                  </div>
                </li>
              )}
              {(jobPortalCompany.contact_name || jobPortalCompany.contact_surname) && (
                <li>
                  <div className="text-sm font-semibold">{t('text1724')}</div>
                  <p className="text-base font-bold text-primary">
                    {[jobPortalCompany.contact_name, jobPortalCompany.contact_surname]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                </li>
              )}
            </ul>
          </Card>
          {/* {isInitialized && isStudent && jobPortalCompany.email && me?.customer?.slug && (
            <Button className="w-full" onClick={handleSendCurriculum}>
              Zaslať životopis
            </Button>
          )} */}
        </aside>
      </div>

      {/* Company offers */}
      <section className="flex flex-col gap-4">
        <h2 className="text-xl font-bold">{t('text2463')}</h2>
        {companyOffers && companyOffers.data?.length ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {companyOffers.data.map(offer => (
              <JobOfferCardSmall key={offer.id} offer={offer as any} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('text2464')}</p>
        )}
      </section>
    </Container>
  );
};

export default JobCompanyPageClient;
